import { useEffect, useMemo, useState } from "react";
import { canViewHref, normGroups } from "@/lib/acl";
import type { NavItem } from "@/lib/nav";
import { isNavItemActive as isNavItemActiveBase } from "@/lib/personalNav";

export const BESTELLWESEN_CHILD_ITEMS: NavItem[] = [
  {
    key: "uebersicht",
    label: "Übersicht",
    href: "/bestellwesen/uebersicht",
    internal: true,
  },
  {
    key: "bestellungen",
    label: "Bestellungen",
    href: "/bestellwesen/bestellungen",
    internal: true,
    aclHref: "/bestellungen.html",
  },
  {
    key: "materialanforderungen",
    label: "Materialanforderungen",
    href: "/bestellwesen/materialanforderungen",
    internal: true,
    aclHref: "/materialanforderungen.html",
    badgeKeys: ["material-open-urgent-fix", "material-open", "material-in-bearbeitung"],
  },
  {
    key: "anfragen",
    label: "Anfragen",
    href: "/bestellwesen/anfragen",
    internal: true,
    aclHref: "/anfragen.html",
    badgeKeys: ["anfragen-answers"],
  },
  {
    key: "artikel",
    label: "Artikel",
    href: "/bestellwesen/artikel",
    internal: true,
    aclHref: "/artikel.html",
  },
  {
    key: "rechnungs-postfach",
    label: "Rechnungs-Postfach",
    href: "/bestellwesen/rechnungs-postfach",
    internal: true,
    aclHref: "/invoice-mail.html",
  },
  {
    key: "eingangsrechnung-artikel",
    label: "Eingangsrechnung-Artikel",
    href: "/bestellwesen/eingangsrechnung-artikel",
    internal: true,
    aclHref: "/invoice-mail.html",
  },
];

/** Standalone-Pfade für Nutzer mit nur einem sichtbaren Bereich (z. B. Monteure → Material). */
export const BESTELLWESEN_STANDALONE_HREFS: Record<string, string> = {
  uebersicht: "/bestellwesen/uebersicht",
  bestellungen: "/bestellungen",
  materialanforderungen: "/materialanforderungen",
  anfragen: "/anfragen",
  artikel: "/artikel",
  "rechnungs-postfach": "/rechnungs-postfach",
  "eingangsrechnung-artikel": "/eingangsrechnung-artikel",
};

export const BESTELLWESEN_HUB_NAV: NavItem = {
  key: "bestellwesen",
  label: "Bestellwesen",
  href: "/bestellwesen/uebersicht",
  internal: true,
  aclHref: "/bestellungen.html",
  badgeKeys: [
    "material-open-urgent-fix",
    "material-open",
    "material-in-bearbeitung",
    "anfragen-answers",
  ],
  activePrefixes: [
    "/bestellwesen",
    "/bestellungen",
    "/materialanforderungen",
    "/anfragen",
    "/artikel",
    "/rechnungs-postfach",
    "/eingangsrechnung-artikel",
  ],
};

const MONTEUR_MATERIAL_NAV: NavItem = {
  key: "materialanforderungen",
  label: "Materialanforderungen",
  href: BESTELLWESEN_STANDALONE_HREFS.materialanforderungen ?? "/materialanforderungen",
  internal: true,
  aclHref: "/materialanforderungen.html",
  badgeKeys: ["material-delivered-unseen"],
  activePrefixes: ["/materialanforderungen", "/bestellwesen/materialanforderungen"],
};

function isMonteurGroup(groups: string[]): boolean {
  return normGroups(groups).some((group) => group.toLowerCase().includes("monteur"));
}

/** Büro/Admin/Tester u. a. behalten den Bestellwesen-Hub. */
function hasElevatedBestellwesenGroup(groups: string[]): boolean {
  return normGroups(groups).some((group) => {
    const g = group.toLowerCase();
    return (
      g.includes("buero") ||
      g.includes("büro") ||
      g.includes("app-admin") ||
      g.includes("app-tester") ||
      g.includes("abrechnung") ||
      g.includes("geschaeftsfuehrung") ||
      g.includes("geschäftsführung")
    );
  });
}

export async function resolveVisibleBestellwesenChildItems(groups: string[]): Promise<NavItem[]> {
  const childItems = BESTELLWESEN_CHILD_ITEMS.filter((item) => item.key !== "uebersicht");
  const childChecks = await Promise.all(
    childItems.map((item) => canViewHref(item.aclHref ?? item.href, groups)),
  );
  return childItems.filter((_, index) => childChecks[index]);
}

/** Monteure ohne Büro/Admin: direkt Materialanforderungen, kein Bestellwesen-Hub. */
export function isMonteurMaterialOnlyBestellwesen(
  groups: string[],
  childItems: NavItem[],
): boolean {
  if (!isMonteurGroup(groups) || hasElevatedBestellwesenGroup(groups)) return false;
  if (childItems.length === 0) return false;
  // Auch wenn ACL weitere Bestellwesen-Seiten freigäbe: Monteure bleiben auf MA.
  return childItems.some((item) => item.key === "materialanforderungen");
}

export async function resolveVisibleBestellwesenItems(groups: string[]): Promise<NavItem[]> {
  const childItems = await resolveVisibleBestellwesenChildItems(groups);
  if (childItems.length === 0) return [];

  if (isMonteurMaterialOnlyBestellwesen(groups, childItems)) {
    return [MONTEUR_MATERIAL_NAV];
  }

  const overviewItem = BESTELLWESEN_CHILD_ITEMS.find((item) => item.key === "uebersicht");
  return overviewItem ? [overviewItem, ...childItems] : childItems;
}

/** Nav-Einträge: Hub bei ≥2 sichtbaren Seiten, sonst die einzelne Seite direkt. */
export async function resolveBestellwesenNavItems(groups: string[]): Promise<NavItem[]> {
  const childItems = await resolveVisibleBestellwesenChildItems(groups);
  if (childItems.length === 0) return [];

  if (isMonteurMaterialOnlyBestellwesen(groups, childItems)) {
    return [MONTEUR_MATERIAL_NAV];
  }

  const visible = await resolveVisibleBestellwesenItems(groups);
  if (visible.length === 1) {
    const only = visible[0];
    if (!only) return [];
    return [
      {
        ...only,
        href: BESTELLWESEN_STANDALONE_HREFS[only.key] ?? only.href,
      },
    ];
  }
  return [BESTELLWESEN_HUB_NAV];
}

export function isNavItemActive(item: NavItem, pathname: string): boolean {
  return isNavItemActiveBase(item, pathname);
}

export function useBestellwesenNavigationWithUser(groups: string[]) {
  const normalizedGroups = useMemo(() => normGroups(groups), [groups]);
  const [visibleItems, setVisibleItems] = useState<NavItem[]>([]);
  const [monteurMaterialOnly, setMonteurMaterialOnly] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const childItems = await resolveVisibleBestellwesenChildItems(normalizedGroups);
        const items = await resolveVisibleBestellwesenItems(normalizedGroups);
        if (!cancelled) {
          setMonteurMaterialOnly(isMonteurMaterialOnlyBestellwesen(normalizedGroups, childItems));
          setVisibleItems(items);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [normalizedGroups]);

  return {
    visibleItems,
    showSidebar: visibleItems.length >= 2,
    monteurMaterialOnly,
    loading,
  };
}
